import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ListTodo, Loader2, Pencil, Plus, RotateCcw, SkipForward } from 'lucide-react';
import CreateNetworkingTaskModal from './CreateNetworkingTaskModal';
import TaskOriginBadge from '../Tasks/TaskOriginBadge';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  isClosedTaskStatus,
  isOpenTaskStatus,
  taskStatusUpdatePatch,
  taskTypeLabel,
  type NetworkingTaskRow,
  type NetworkingTaskStatus,
} from '../../lib/networkingTasks';

interface Props {
  subscriberId: string;
  contactEmail: string;
  /** Subscriber detail Tasks tab — full-width layout */
  asTab?: boolean;
  /** Refresh tab badge on parent after load / status change */
  onTasksChanged?: () => void;
}

function formatDue(dueAt: string): string {
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return dueAt;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function SubscriberNetworkingTasksSection({
  subscriberId,
  contactEmail,
  asTab = false,
  onTasksChanged,
}: Props) {
  const [tasks, setTasks] = useState<NetworkingTaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<NetworkingTaskRow | null>(null);
  const canCreate = contactEmail.trim().includes('@');
  const onTasksChangedRef = useRef(onTasksChanged);
  useEffect(() => {
    onTasksChangedRef.current = onTasksChanged;
  }, [onTasksChanged]);

  const load = useCallback(async () => {
    if (!subscriberId) {
      setTasks([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('subscriber_id', subscriberId)
        .order('sort_order', { ascending: true })
        .order('due_at', { ascending: true })
        .limit(50);
      if (error) throw error;
      setTasks((data ?? []) as NetworkingTaskRow[]);
      onTasksChangedRef.current?.();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [subscriberId]);

  useEffect(() => {
    void load();
  }, [load]);

  const setStatus = async (id: string, nextStatus: NetworkingTaskStatus) => {
    setUpdatingId(id);
    try {
      const { error } = await supabase
        .from('tasks')
        .update(taskStatusUpdatePatch(nextStatus))
        .eq('id', id);
      if (error) throw error;
      if (nextStatus === 'pending') {
        toast.success('Task marked pending');
      }
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setUpdatingId(null);
    }
  };

  const closeModal = () => {
    setCreateOpen(false);
    setEditingTask(null);
  };

  const openCount = tasks.filter((t) => isOpenTaskStatus(t.status)).length;

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide flex items-center gap-2">
          <ListTodo className="w-4 h-4 text-emerald-500" />
          {asTab ? 'Tasks' : 'Networking tasks'}
          {openCount > 0 ? (
            <span className="normal-case font-medium text-amber-700 dark:text-amber-300">
              ({openCount} open)
            </span>
          ) : null}
        </h2>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            disabled={!canCreate}
            title={canCreate ? 'Add task' : 'Add an email to this connection first'}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            Add task
          </button>
          <Link
            to="/tasks"
            className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline"
          >
            Global queue
          </Link>
        </div>
      </div>
      {loading ? (
        <div className="px-6 py-8 flex justify-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : tasks.length === 0 ? (
        <div className={`${asTab ? 'px-6 py-10 text-center max-w-lg mx-auto' : 'px-6 py-6'}`}>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No tasks yet. BookTime bookings create a pre-screen task automatically (due at meeting time).
          </p>
          {canCreate ? (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-emerald-500 text-white hover:bg-emerald-600"
            >
              <Plus className="w-4 h-4" />
              Add task
            </button>
          ) : null}
        </div>
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {tasks.map((task) => {
            const busy = updatingId === task.id;
            return (
              <li
                key={task.id}
                className="px-6 py-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {task.title || taskTypeLabel(task.task_type)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Due {formatDue(task.due_at)} · {task.status}
                  </p>
                  <div className="mt-1">
                    <TaskOriginBadge task={task} />
                  </div>
                  {task.notes?.trim() ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                      {task.notes.trim()}
                    </p>
                  ) : null}
                </div>
                <div className="flex shrink-0 gap-1">
                  {isOpenTaskStatus(task.status) ? (
                    <>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void setStatus(task.id, 'done')}
                        className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                        title="Done"
                      >
                        {busy ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void setStatus(task.id, 'skipped')}
                        className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Skip"
                      >
                        <SkipForward className="w-4 h-4" />
                      </button>
                    </>
                  ) : isClosedTaskStatus(task.status) ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void setStatus(task.id, 'pending')}
                      className="p-1.5 rounded-md text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30"
                      title="Mark pending again"
                    >
                      {busy ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RotateCcw className="w-4 h-4" />
                      )}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setEditingTask(task)}
                    className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Edit task"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <CreateNetworkingTaskModal
        isOpen={createOpen || editingTask != null}
        onClose={closeModal}
        subscriberId={subscriberId}
        contactEmail={contactEmail}
        task={editingTask}
        onSuccess={() => void load()}
      />
    </section>
  );
}
